import {
    timeout,
    appendParamsToUrl,
    getUrlParams,
    get
} from 'widgets/toolbox/util';
import { submitFormJson } from 'widgets/toolbox/ajax';

const TAU_UNIQUE = 'data-tau-unique';

/**
 * @typedef {typeof import('widgets/Widget').default} Widget
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/ShippingForm').default>>} shippingForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/BillingForm').default>>} billingForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/InputCheckbox').default>>} inputCheckbox
 *
 */

/**
 * @param {Widget} Widget mixin to handle focus
 * @returns {typeof CheckoutMgr} Checkout Manager widget
 */

export default function (Widget) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class CheckoutMgr
     * @augments Widget
     * @property {string} shippingFormID - Shipping form ID
     * @property {string} billingFormID - Billing form ID
     * @property {string} stepsList - List of checkot steps
     * @property {object} data-basket - Basket data object
     * @property {object} data-customer - Customer data object
     * @property {string} data-url-checkout - Checkout begin URL
     * @property {string} data-url-place-order - Place order URL
     * @classdesc CheckoutMgr component on checkout page. Widget has next functionality:
     * 1. Manage checkout steps activation/deactivation/submission
     * 2. Update summary block
     * 3. Handles popstate event
     *
     * Widget has next relationship:
     * * From initialization method renders shipping methods list by calling {@link ShippingForm#updateState}
     * * Activates shipping/billing step by calling {@link ShippingForm#activate}/{@link BillingForm#activate}
     * * Submits shipping/billing form by calling {@link ShippingForm#submit}/{@link BillingForm#submit}
     * * Checks pravicy checkbox status before summary submission by calling {@link InputCheckbox#validate}
     * * Sets billing errors by calling {@link BillingForm#setError}
     * * Listens {@link ShippingForm#event:updateshippingform} to update summary
     * * Listens {@link ShippingForm#submit}/{@link BillingForm#submit} to update summary and go to the next step
     * * Listens `dom#click` on the next step button to go to the next step
     * * Listens `dom#click` on the edit step button to edit one of the previous step
     *
     * @example <caption>Checkout manager</caption>
     * <div
     *    id="checkout-main"
     *    data-checkout-get-url="${URLUtils.https('CheckoutServices-Get')}"
     *    data-url-place-order="${URLUtils.url('CheckoutServices-PlaceOrder')}"
     *    data-url-checkout="${URLUtils.url('Checkout-Begin')}" data-widget="checkoutMgr"
     *    data-basket="${JSON.stringify(pdict.order)}" data-customer="${JSON.stringify(pdict.customer)}"
     *  >
     *    ...
     *    <isinclude template="checkout/shipping/shipping" />
     *    <isinclude template="checkout/billing/billing" />
     *    <isinclude template="checkout/summary/summary" />
     *    ...
     *    <isinclude template="m/checkout/orderTotalSummary" />
     *    ...
     *    <isinclude template="m/checkout/orderProductSummary" />
     *    ...
     * </div>
     */
    class CheckoutMgr extends Widget {
        prefs() {
            return {
                shippingFormID: 'dwfrm_shipping',
                billingFormID: 'dwfrm_billing',
                stepsList: 'shipping,billing,summary',
                paymentAccordionErrorLabel: 'errorMessageLabel',
                basket: {},
                customer: {},
                urlCheckout: '',
                urlPlaceOrder: '',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         * * render shipping methods list
         * * activate current step
         * * init events
         * * init properties
         * @returns {void}
         */
        init() {
            this.onDestroy(timeout(() => {
                const basket = this.prefs().basket;

                this.order = basket;
                this.customer = this.prefs().customer;

                this.getById(
                    this.prefs().shippingFormID,
                    (/** @type {shippingForm} */shippingFormComponent) => shippingFormComponent.updateState(basket)
                );
                const urlParams = getUrlParams();
                if (basket && basket.steps && basket.steps.billing && !basket.steps.billing.iscompleted) {
                    this.currentStep = 'shipping';
                } else if (typeof urlParams.step === 'string') {
                    this.currentStep = urlParams.step;
                } else if (urlParams.stage === 'payment') {
                    this.currentStep = 'billing';
                } else {
                    this.currentStep = 'shipping';
                }

                this.activateStepByName(this.currentStep, true);

                this.updateSummary(basket);
            }));

            this.initEvents();
        }

        /**
         * @description Initialize widget events
         * @returns {void}
         */
        initEvents() {
            this.ev('popstate', () => {
                const urlParams = getUrlParams();
                if (typeof urlParams.step === 'string') {
                    this.activateStepByName(urlParams.step);
                }
            }, window);
        }

        /**
         * @description Activates checkout step by step name
         * @param {string} name - Name of step
         * @param {boolean} [historyReplace] - If we need to replace history state instead of push it
         * @returns {void}
         */
        activateStepByName(name, historyReplace = false) {
            let isStepActivated = false;
            let wineClubCheckboxEle = document.getElementsByClassName('check-wine-club') ? document.getElementsByClassName('check-wine-club')[0] : null;
            if(wineClubCheckboxEle) {
                if(name === 'summary') {
                    wineClubCheckboxEle.classList.remove('h-hidden');
                } else if(!wineClubCheckboxEle.classList.contains('h-hidden')) {
                    wineClubCheckboxEle.classList.add('h-hidden');
                }
            }
            
            this.prefs().stepsList.split(',').forEach(step => {
                if (step === name) {
                    isStepActivated = true;
                    this.currentStep = step;
                    this[step + 'Activate'].call(this);

                    const urlParams = getUrlParams();
                    if (urlParams.step !== step) {
                        window.history[historyReplace ? 'replaceState' : 'pushState'](
                            undefined, '', appendParamsToUrl(this.prefs().urlCheckout, { step })
                        );
                    }
                } else {
                    this[step + 'DeActivate'].call(this, !isStepActivated);
                }
            });
            this.updateSummary();
        }

        /**
         * @description Update summary by event from child element
         * @param {Widget} widget - child widget, that fired event
         * @param {object} order - order data
         * @listens ShippingForm#updateshippingform
         * @returns {void}
         */
        updateSummaryEv(widget, order) {
            this.updateSummary(order);
        }

        /**
         * @description Update summary block
         * @param {object} [order] order data
         * @emits "order.updated"
         * @emits "checkout.step.updated"
         * @returns {void}
         */
        updateSummary(order) {
            if (order) {
                this.order = order;
                if (order.billing
                    && order.billing.payment
                    && order.billing.payment.selectedPaymentInstruments
                ) {
                    order.billing.payment.selectedPaymentInstruments.forEach(pi => {
                        pi[pi.paymentMethod] = true;
                    });
                }
            }

            if (this.order) {
                this.order.isShipping = this.currentStep === 'shipping';
                this.order.isBilling = this.currentStep === 'billing';
                this.order.isSummary = this.currentStep === 'summary';
                this.eventBus().emit('order.updated', this.order);
            }

            const shipping = (order || this.order || this.prefs().basket).shipping;
            const hasShippingMethods = Boolean(get(shipping, '0.applicableShippingMethods.length'));

            this.eventBus().emit('checkout.step.updated', {
                [String(this.currentStep)]: true,
                disabled: hasShippingMethods ? '' : 'disabled',
                hasShippingMethods
            });
        }

        /**
         * @description Submit current step
         * @listens dom#click
         * @returns {void}
         */
        submitCurrentStep() {
            this[this.currentStep + 'Submit'].call(this);
        }

        /**
         * @description Activate next step
         * @returns {void}
         */
        nextStep() {
            const steps = this.prefs().stepsList.split(',');

            // eslint-disable-next-line no-empty
            while (steps.length && this.currentStep !== steps.shift()) { }

            if (steps.length) {
                this.activateStepByName(steps.shift() + '');
            } else {
                this.activateStepByName(this.prefs().stepsList.split(',').shift() + '');
            }
        }

        /**
         * @description Activate shipping step
         * @returns {void}
         */
        shippingActivate() {
            this.getById(
                this.prefs().shippingFormID,
                (/** @type {shippingForm} */shippingFormComponent) => shippingFormComponent.activate({
                    order: this.order,
                    customer: this.customer
                })
            );

            this.ref('shippingStep').attr(TAU_UNIQUE, 'checkout_shipping');

            this.ref('shippingSection').show();
            this.ref('shippingSummary').hide();
            this.callIfExists('focusFirstInput', 'shippingSection'); // class AccessibilityFocusMixin, method focusFirstInput
        }

        /**
         * @description Deactivate shipping step
         * @returns {void}
         */
        shippingDeActivate() {
            this.ref('shippingStep').attr(TAU_UNIQUE, false);

            this.ref('shippingSection').hide();
            this.ref('shippingSummary').show();
        }

        /**
         * @description Submit shipping form
         * @returns {void}
         */
        shippingSubmit() {
            this.getById(this.prefs().shippingFormID, (/** @type {shippingForm} */shippingFormComponent) => {
                shippingFormComponent.submit();
            });
        }

        /**
         *
         * @description Shipping submission result handler
         * @param {shippingForm} shippingForm - Shipping form widget
         * @param {object} data - Submission response from server
         * @param {object} [data.order] - Order object
         * @emits "checkout.shipping.step.submitted"
         * @listens ShippingForm#submit
         * @returns {void}
         */
        shippingSubmitted(shippingForm, data) {
            this.eventBus().emit('checkout.shipping.step.submitted', data);

            this.updateSummaryAndNext(shippingForm, data);
        }

        /**
         * @description Activate billing step
         * @returns {void}
         */
        billingActivate() {
            this.getById(
                this.prefs().billingFormID,
                (/** @type {billingForm} */billingFormComponent) => billingFormComponent.activate({
                    order: this.order,
                    customer: this.customer
                })
            );

            this.ref('billingStep').attr(TAU_UNIQUE, 'checkout_billing');

            this.ref('billingSection').show();
            this.ref('billingSummary').hide();

            const urlParams = getUrlParams();
            if (urlParams.paymentError) {
                this.ref(this.prefs().billingFormID).data('setError', urlParams.paymentError);
            }
            this.callIfExists('focusFirstInput', 'billingSection'); // class AccessibilityFocusMixin, method focusFirstInput
        }

        /**
         * @description Deactivate billing step
         * @param {boolean} isNextStepActive - Flag to detect is next step activation needed
         * @returns {void}
         */
        billingDeActivate(isNextStepActive) {
            this.ref('billingSection').hide();

            this.ref('billingStep').attr(TAU_UNIQUE, false);

            if (isNextStepActive) {
                this.ref('billingSummary').show();
            } else {
                this.ref('billingSummary').hide();
            }
        }

        /**
         * @description Submit billing form
         * @returns {void}
         */
        billingSubmit() {
            this.getById(this.prefs().billingFormID, (/** @type {billingForm} */billingFormComponent) => {
                billingFormComponent.submit();
            });
        }

        /**
         *
         * @description Billing submission result handler
         * @param {billingForm} billingForm - Billing form widget
         * @param {object} data - Submission response from server
         * @param {object} [data.order] - Order object
         * @emits "checkout.billing.step.submitted"
         * @listens BillingForm#submit
         * @returns {void}
         */
        billingSubmitted(billingForm, data) {
            this.eventBus().emit('checkout.billing.step.submitted', data);

            this.updateSummaryAndNext(billingForm, data);
        }

        /**
         * @description Activate summary step
         * @returns {void}
         */
        summaryActivate() {
            this.ref('summaryStep').attr(TAU_UNIQUE, 'checkout_order_review');

            this.ref('confirmationSummary').show();
            this.callIfExists('focusFirstInput', 'confirmationSummary'); // class AccessibilityFocusMixin, method focusFirstInput
        }

        /**
         * @description Deactivate summary step
         * @returns {void}
         */
        summaryDeActivate() {
            this.ref('summaryStep').attr(TAU_UNIQUE, false);

            this.ref('confirmationSummary').hide();
            this.ref('summaryError').hide();
        }

        /**
         * @description Submit summary form
         * @returns {void}
         */
        summarySubmit() {
            this.ref('summaryError').hide();

            this.getById('agreeToPrivacy', (/** @type {inputCheckbox} */ agreeToPrivacy) => {
                if (!agreeToPrivacy.validate()) {
                    return;
                }

                submitFormJson(
                    this.prefs().urlPlaceOrder
                ).then((response) => {
                    if (response && response.redirectUrl) {
                        window.location.assign(response.redirectUrl);
                    } else if (response && response.error) {
                        if (response.errorStage) {
                            if (['billing', 'payment'].includes(response.errorStage.stage)) {
                                this.activateStepByName('billing');
                                this.getById(this.prefs().billingFormID, (/** @type {billingForm} */billingFormComponent) => {
                                    billingFormComponent.setError(response.errorMessage);
                                });
                            }
                        } else if (response.errorMessage) {
                            this.ref('summaryError').show().setText(response.errorMessage);
                        }
                    } else if (response && response.continueUrl) {
                        this.summarySubmitted();
                        window.location.assign(
                            appendParamsToUrl(response.continueUrl, {
                                ID: response.orderID,
                                token: response.orderToken
                            })
                        );
                    }
                });
            });
        }

        /**
         * @description Summary submission result handler
         * @emits "checkout.summary.step.submitted"
         * @returns {void}
         */
        summarySubmitted() {
            this.eventBus().emit('checkout.summary.step.submitted', this);
        }

        /**
         * @description Update summary block and go to the next step
         * @param {shippingForm|billingForm} formWidget - Billing form widget
         * @param {object} data - Submission response from server
         * @param {object} [data.order] - Order object
         * @returns {void}
         */
        updateSummaryAndNext(formWidget, data) {
            if (data.order) {
                this.updateSummary(data.order);
                this.nextStep();
            }
        }

        /**
         * @description Handle click on step edit button
         * @param {refElement} el - Edit button ref element
         * @listens dom#click
         * @returns {void}
         */
        handleEditStep(el) {
            switch (el.data('step')) {
                case 'shipping':
                    this.activateStepByName('shipping');
                    break;
                case 'billing':
                    this.activateStepByName('billing');
                    break;
                default:
                    break;
            }
        }
    }

    return CheckoutMgr;
}
